import React, { Component } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Close, Save, Refresh, Info, Add, Delete } from "@mui/icons-material";
import { Link } from "react-router-dom";
import axios from "axios";
import Api from "../../customlib/Api";

/**
 * Halaman CS Ticket Settings.
 * Endpoint:
 *   GET  /api/cs/ticket/settings  -> { settings:[{key,value,description}], schema:{key:{type,label,...}} }
 *   POST /api/cs/ticket/settings  -> body: { settings:[{key,value,description?}] }
 */
class CsTicketSettings extends Component {
    state = {
        loading: false,
        saving: false,
        settings: [],        // rows dari DB + schema defaults
        schema: {},          // metadata per key
        edited: {},          // { key: newValue }
        fieldErrors: {},     // { key: "error message" } - validasi client
        snackbar: { open: false, message: "", severity: "success" },
        errorBanner: "",
    };

    componentDidMount() {
        this.loadSettings();
    }

    loadSettings = () => {
        this.setState({ loading: true, errorBanner: "" });
        axios
            .get(Api.csTicketListSettings(), Api.getRequestHeader())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    const settings = (data.data && data.data.settings) || [];
                    const schema = (data.data && data.data.schema) || {};
                    this.setState({
                        loading: false,
                        settings,
                        schema,
                        edited: {},
                        fieldErrors: {},
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorBanner: data.message || "Gagal memuat settings",
                    });
                }
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    errorBanner:
                        (err.response && err.response.data && err.response.data.message) ||
                        err.message ||
                        "Network error",
                });
            });
    };

    getCurrentValue = (row) => {
        if (Object.prototype.hasOwnProperty.call(this.state.edited, row.key)) {
            return this.state.edited[row.key];
        }
        return row.value != null ? row.value : "";
    };

    setFieldValue = (key, value) => {
        const edited = { ...this.state.edited, [key]: value };
        const fieldErrors = { ...this.state.fieldErrors };
        const err = this.validateField(key, value);
        if (err) fieldErrors[key] = err;
        else delete fieldErrors[key];
        this.setState({ edited, fieldErrors });
    };

    validateField = (key, value) => {
        const meta = this.state.schema[key];
        if (!meta) return "";

        if (meta.required && (value === "" || value == null)) {
            return "Wajib diisi";
        }
        if (value === "" || value == null) return "";

        switch (meta.type) {
            case "number":
            case "minutes":
            case "hours": {
                if (!/^-?\d+$/.test(String(value))) return "Harus angka bulat";
                const num = parseInt(value, 10);
                if (meta.min != null && num < meta.min) return `Minimum ${meta.min}`;
                if (meta.max != null && num > meta.max) return `Maksimum ${meta.max}`;
                return "";
            }
            case "url": {
                try {
                    const u = new URL(value);
                    if (!u.protocol.startsWith("http")) return "URL harus http/https";
                    return "";
                } catch (e) {
                    return "URL tidak valid";
                }
            }
            case "wa_number": {
                const clean = String(value).replace(/[\s+\-]/g, "");
                if (!/^[0-9]{8,15}$/.test(clean)) return "Hanya digit, 8-15 karakter";
                return "";
            }
            case "json_array": {
                try {
                    const parsed = JSON.parse(value);
                    if (!Array.isArray(parsed)) return "Harus JSON array";
                    return "";
                } catch (e) {
                    return "JSON tidak valid";
                }
            }
            default:
                return "";
        }
    };

    handleSave = () => {
        const fieldErrors = {};
        (this.state.settings || []).forEach((row) => {
            const value = this.getCurrentValue(row);
            const err = this.validateField(row.key, value);
            if (err) fieldErrors[row.key] = err;
        });

        if (Object.keys(fieldErrors).length > 0) {
            this.setState({
                fieldErrors,
                snackbar: {
                    open: true,
                    message: "Ada field yang belum valid. Mohon perbaiki dulu.",
                    severity: "error",
                },
            });
            return;
        }

        // Kirim semua row (pakai current value) — backend akan validasi ulang.
        const payload = {
            settings: (this.state.settings || []).map((row) => ({
                key: row.key,
                value: String(this.getCurrentValue(row) ?? ""),
                description: row.description || null,
            })),
        };

        this.setState({ saving: true, errorBanner: "" });
        axios
            .post(
                Api.csTicketUpdateSettings(),
                payload,
                Api.getRequestHeaderJson(),
            )
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    const settings = (data.data && data.data.settings) || [];
                    const schema = (data.data && data.data.schema) || this.state.schema;
                    this.setState({
                        saving: false,
                        settings,
                        schema,
                        edited: {},
                        fieldErrors: {},
                        snackbar: {
                            open: true,
                            message: "Settings berhasil disimpan",
                            severity: "success",
                        },
                    });
                } else {
                    this.setState({
                        saving: false,
                        errorBanner: data.message || "Gagal menyimpan settings",
                        snackbar: {
                            open: true,
                            message: data.message || "Gagal menyimpan",
                            severity: "error",
                        },
                    });
                }
            })
            .catch((err) => {
                const msg =
                    (err.response && err.response.data && err.response.data.message) ||
                    err.message ||
                    "Network error";
                this.setState({
                    saving: false,
                    errorBanner: msg,
                    snackbar: { open: true, message: msg, severity: "error" },
                });
            });
    };

    // ==============================================================
    // Render helpers
    // ==============================================================

    renderField = (row) => {
        const meta = this.state.schema[row.key] || { type: "string", label: row.key };
        const value = this.getCurrentValue(row);
        const error = this.state.fieldErrors[row.key] || "";
        const label = meta.label || row.key;

        const commonProps = {
            fullWidth: true,
            size: "small",
            value: value == null ? "" : value,
            onChange: (e) => this.setFieldValue(row.key, e.target.value),
            error: !!error,
            helperText: error || meta.description || row.description || "",
            variant: "outlined",
            InputLabelProps: { shrink: true },
        };

        switch (meta.type) {
            case "minutes":
                return (
                    <TextField
                        {...commonProps}
                        type="number"
                        label={label}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">menit</InputAdornment>,
                            inputProps: { min: meta.min, max: meta.max, step: 1 },
                        }}
                    />
                );
            case "hours":
                return (
                    <TextField
                        {...commonProps}
                        type="number"
                        label={label}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">jam</InputAdornment>,
                            inputProps: { min: meta.min, max: meta.max, step: 1 },
                        }}
                    />
                );
            case "number":
                return (
                    <TextField
                        {...commonProps}
                        type="number"
                        label={label}
                        InputProps={{
                            inputProps: { min: meta.min, max: meta.max, step: 1 },
                        }}
                    />
                );
            case "url":
                return (
                    <TextField
                        {...commonProps}
                        type="url"
                        label={label}
                        placeholder="https://..."
                    />
                );
            case "wa_number":
                return (
                    <TextField
                        {...commonProps}
                        label={label}
                        placeholder="6281234567890"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">📱</InputAdornment>
                            ),
                        }}
                    />
                );
            case "json_array":
                return this.renderJsonArrayField(row, meta, value, error);
            case "textarea":
                return (
                    <TextField
                        {...commonProps}
                        label={label}
                        multiline
                        rows={4}
                    />
                );
            case "string":
            default:
                return <TextField {...commonProps} label={label} />;
        }
    };

    // JSON array sebagai list chip - user bisa add/remove per item
    renderJsonArrayField = (row, meta, value, error) => {
        let items = [];
        try {
            const parsed = JSON.parse(value || "[]");
            if (Array.isArray(parsed)) items = parsed;
        } catch (e) {
            items = [];
        }

        console.log("CO_ISSUE", items);

        const onAdd = () => {
            const next = [...items, {issue_co: "", person_incharge: ""}];
            this.setFieldValue(row.key, JSON.stringify(next));
        };

        const onChange = (idx, newVal) => {
            const next = items.slice();
            next[idx].issue_co = newVal;
            this.setFieldValue(row.key, JSON.stringify(next));
        };

        const onChangePenanggungJawab = (idx, newVal) => {
            const next = items.slice();
            next[idx].person_incharge = newVal;
            this.setFieldValue(row.key, JSON.stringify(next));
        };

        const onRemove = (idx) => {
            const next = items.filter((_, i) => i !== idx);
            this.setFieldValue(row.key, JSON.stringify(next));
        };

        return (
            <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                    {meta.label || row.key}
                </Typography>
                {(meta.description || row.description) && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {meta.description || row.description}
                    </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
                    {items.length === 0 && (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            Belum ada opsi. Klik tombol "Tambah Opsi".
                        </Typography>
                    )}
                    {items.map((item, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={item.issue_co || ""}
                                onChange={(e) => onChange(idx, e.target.value)}
                                placeholder={`Opsi #${idx + 1}`}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                value={item.person_incharge || ""}
                                onChange={(e) => onChangePenanggungJawab(idx, e.target.value)}
                                placeholder={`Penanggung Jawab #${idx + 1}`}
                            />
                            <Tooltip title="Hapus opsi">
                                <IconButton size="small" color="error" onClick={() => onRemove(idx)}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={onAdd}
                >
                    Tambah Opsi
                </Button>
                {error && (
                    <Typography variant="caption" color="error" display="block" mt={1}>
                        {error}
                    </Typography>
                )}
                <Chip
                    size="small"
                    label={`${items.length} opsi`}
                    sx={{ mt: 1 }}
                    color={items.length > 0 ? "primary" : "default"}
                />
            </Box>
        );
    };

    // ==============================================================
    // Group settings by schema.group
    // ==============================================================
    groupSettings = () => {
        const groups = {};
        const unknownKey = "Lainnya";

        (this.state.settings || []).forEach((row) => {
            const meta = this.state.schema[row.key];
            const g = (meta && meta.group) || unknownKey;
            if (!groups[g]) groups[g] = [];
            groups[g].push(row);
        });

        // Preferred order
        const preferred = ["Ticket", "Auto-Reply", "WA Number", "URL", "Advanced", unknownKey];
        const ordered = {};
        preferred.forEach((g) => {
            if (groups[g]) ordered[g] = groups[g];
        });
        Object.keys(groups).forEach((g) => {
            if (!ordered[g]) ordered[g] = groups[g];
        });
        return ordered;
    };

    render() {
        const { loading, saving, errorBanner, snackbar, edited, fieldErrors } = this.state;
        const dirty = Object.keys(edited).length > 0;
        const hasErrors = Object.keys(fieldErrors).length > 0;
        const grouped = this.groupSettings();

        return (
            <Paper sx={{ padding: "20px", margin: "10px" }}>
                <Box>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item sm={10} xs={8}>
                            <Typography variant="h5" fontWeight={600}>
                                CS Ticket Settings
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Konfigurasi sistem ticketing CS (auto-close, expire, nomor official, URL, dll.)
                            </Typography>
                        </Grid>
                        <Grid item sm={2} xs={4} textAlign="right">
                            <Tooltip title="Reload">
                                <span>
                                    <IconButton onClick={this.loadSettings} disabled={loading || saving}>
                                        <Refresh />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Tutup">
                                {this.props.onClose ? (
                                    <IconButton color="error" onClick={this.props.onClose}>
                                        <Close />
                                    </IconButton>
                                ) : (
                                    <Link to="/">
                                        <IconButton color="error">
                                            <Close />
                                        </IconButton>
                                    </Link>
                                )}
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loading && <LinearProgress sx={{ mb: 2 }} />}
                {errorBanner && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ errorBanner: "" })}>
                        {errorBanner}
                    </Alert>
                )}

                <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
                    Perubahan langsung aktif di sistem setelah disimpan. Scheduler tetap jalan otomatis.
                    Set nilai <b>0</b> (untuk auto-close) atau biarkan kosong (jika tidak required) untuk menonaktifkan fitur terkait.
                </Alert>

                {Object.keys(grouped).map((groupName) => (
                    <Box key={groupName} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "primary.main" }}>
                            {groupName}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                {grouped[groupName].map((row) => {
                                    const meta = this.state.schema[row.key] || {};
                                    const isWide = meta.type === "json_array" || meta.type === "textarea";
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            md={isWide ? 12 : 6}
                                            key={row.key}
                                        >
                                            {this.renderField(row)}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                                <code style={{ fontSize: "10px" }}>{row.key}</code>
                                            </Typography>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Paper>
                    </Box>
                ))}

                <Box sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", py: 2, borderTop: "1px solid #eee", mt: 3 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            {dirty && (
                                <Typography variant="body2" color="warning.main">
                                    Ada {Object.keys(edited).length} perubahan belum disimpan.
                                </Typography>
                            )}
                            {hasErrors && (
                                <Typography variant="body2" color="error">
                                    {Object.keys(fieldErrors).length} field masih invalid.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={4} textAlign="right">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                onClick={this.handleSave}
                                disabled={!dirty || saving || hasErrors || loading}
                            >
                                {saving ? "Menyimpan..." : "Simpan Perubahan"}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        );
    }
}

export default CsTicketSettings;
